"use client"

import type { KeyboardEvent } from "react"
import { useEffect, useRef } from "react"

import { cn } from "@productlane/lib/src/browser"

import type { AudioInputState } from "./use-audio-input"

export function VoiceRecordingBar({
	state,
	levels,
	compact = false,
	onCancel,
	onStop,
}: {
	state: AudioInputState
	levels: number[]
	// Fits the composer it covers instead of growing it, with 32px controls.
	compact?: boolean
	onCancel: () => void
	onStop: () => void
}) {
	const cancelRef = useRef<HTMLButtonElement>(null)
	useEffect(() => {
		cancelRef.current?.focus()
	}, [])
	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === "Escape") {
			event.preventDefault()
			event.stopPropagation()
			onCancel()
		}
	}
	return (
		<div
			role="group"
			aria-label="Voice recording"
			className={cn(
				"absolute inset-0 z-20 flex items-center gap-5 rounded-full bg-white pl-3 text-gray-950 shadow-sm dark:bg-gray-800 dark:text-white",
				compact ? "pr-2" : "min-h-[52px] pr-1.5",
			)}
		>
			<button
				ref={cancelRef}
				type="button"
				aria-label="Cancel recording"
				onClick={onCancel}
				onKeyDown={handleKeyDown}
				className={cn(
					"flex shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 dark:hover:bg-gray-700",
					compact ? "h-8 w-8" : "h-9 w-9",
				)}
			>
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					aria-hidden="true"
				>
					<path
						d="m4 4 12 12M16 4 4 16"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
					/>
				</svg>
			</button>
			<div
				className="flex min-w-0 flex-1 items-center justify-center"
				role="status"
				aria-live="polite"
			>
				{state === "recording" ? (
					<>
						<span className="sr-only">
							Recording. Stop to transcribe, or cancel to discard.
						</span>
						<svg
							className={cn("w-full", compact ? "h-8" : "h-10")}
							viewBox="0 0 600 48"
							preserveAspectRatio="none"
							aria-hidden="true"
						>
							{levels.map((level, index) => (
								<line
									key={index}
									x1={index * 6 + 3}
									x2={index * 6 + 3}
									y1={24 - Math.max(0.5, level * 23)}
									y2={24 + Math.max(0.5, level * 23)}
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									vectorEffect="non-scaling-stroke"
								/>
							))}
						</svg>
					</>
				) : (
					<span className="text-sm text-secondary">
						{state === "requesting"
							? "Waiting for microphone…"
							: "Transcribing…"}
					</span>
				)}
			</div>
			<button
				type="button"
				aria-label="Stop recording"
				onClick={onStop}
				onKeyDown={handleKeyDown}
				disabled={state !== "recording"}
				className={cn(
					"flex shrink-0 items-center justify-center rounded-full bg-black text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-40 dark:bg-white dark:text-black",
					compact ? "h-8 w-8" : "h-10 w-10",
				)}
			>
				<span
					className={cn(
						"rounded-[3px] bg-current",
						compact ? "h-3 w-3" : "h-3.5 w-3.5",
					)}
				/>
			</button>
		</div>
	)
}
